import React, { useEffect, useRef, useState, type ReactNode } from 'react'
import { TriggerTooltip } from 'shared/ui/TriggerTooltip/TriggerTooltip'
import { Typography } from 'shared/ui'
import { getCaretCoordinates } from 'shared/lib/getCaretCoordinates'
import styles from './styles.module.scss'

export type ITriggerTooltipAction = {
  shortcut: string
  renderComponent: (onClose: () => void) => ReactNode
  info?: ReactNode
  infoLabel?: string
}

const DefaultShortcutInfo = ({ shortcut, label }: { shortcut: string; label?: string }) => (
  <div className={styles.shortcutInfo}>
    <Typography ariaLabel={'use'} variant={'body-md-regular'} color={'tertiary'}>
      Use
    </Typography>
    <Typography
      ariaLabel={'shortcut'}
      variant={'body-xs-medium'}
      color={'var(--content-primary-secondary)'}
      className={styles.shortcut}
    >
      {shortcut}
    </Typography>
    {label && (
      <Typography ariaLabel={'use'} variant={'body-md-regular'} color={'tertiary'}>
        {label}
      </Typography>
    )}
  </div>
)

export const useShortcutTooltip = ({
  textFieldElement,
  actions,
}: {
  textFieldElement: HTMLElement | null
  actions: ITriggerTooltipAction[]
}) => {
  const [activeShortcut, setActiveShortcut] = useState<string | null>(null)
  const [coordinates, setCoordinates] = useState<{ x: number; y: number } | null>(null)
  const tooltipContainerRef = useRef<HTMLDivElement | null>(null)

  const closeTriggerTooltip = () => {
    setActiveShortcut(null)
    setCoordinates(null)
  }

  useEffect(() => {
    if (!textFieldElement) return

    const onKeyDown = (e: KeyboardEvent) => {
      const matched = actions.find((a) => a.shortcut === e.key)
      if (matched) {
        setTimeout(() => {
          const coords = getCaretCoordinates()
          setCoordinates(coords)
          setActiveShortcut(matched.shortcut)
        })
      } else {
        closeTriggerTooltip()
      }
    }

    textFieldElement.addEventListener('keydown', onKeyDown)
    return () => textFieldElement.removeEventListener('keydown', onKeyDown)
  }, [textFieldElement, actions])

  useEffect(() => {
    if (!activeShortcut) return

    const handleScroll = (e: Event) => {
      if (e.target instanceof Node && tooltipContainerRef.current?.contains(e.target)) return
      closeTriggerTooltip()
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [activeShortcut])

  const activeAction = actions.find((a) => a.shortcut === activeShortcut)

  const triggerTooltip = (
    <TriggerTooltip
      placement={'bottom-start'}
      trigger={{
        getBoundingClientRect: () => new DOMRect(coordinates?.x, coordinates?.y || 0, 0, 0),
      }}
      open={!!coordinates && !!activeAction}
      component={
        activeAction && (
          <div ref={tooltipContainerRef}>{activeAction.renderComponent(closeTriggerTooltip)}</div>
        )
      }
      color={'white'}
      onClickAway={closeTriggerTooltip}
      overflowClassName={styles.wrapTooltip}
      id={'shortcutTooltip'}
      marginBottom={0}
    />
  )

  const infos = actions.map((action) => {
    if (action.info) return action.info
    return (
      <DefaultShortcutInfo
        key={action.shortcut}
        shortcut={action.shortcut}
        label={action.infoLabel}
      />
    )
  })

  return { triggerTooltip, infos }
}
